import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaCopy } from "react-icons/fa";
import { toast } from "react-toastify";

/**
 * CopyableId Component
 * Displays a truncated ID with hover tooltip showing full ID and copy functionality
 *
 * @param {string} id - The full ID to display
 * @param {string} label - Label for toast message (e.g., "Payment ID", "Order ID")
 * @param {number} truncateLength - Number of characters to show before truncation (default: 8)
 * @param {string} placement - Tooltip placement (default: "top")
 * @param {function} onCopy - Optional callback when ID is copied
 */
const CopyableId = ({
  id,
  label = "ID",
  truncateLength = 8,
  placement = "top",
  onCopy,
}) => {
  if (!id) {
    return <span className="text-muted">-</span>;
  }

  const copyToClipboard = (text, label) => {
    try {
      navigator.clipboard.writeText(text).then(
        () => {
          toast.success(`${label} copied to clipboard!`);
          if (onCopy) onCopy(text);
        },
        (err) => {
          console.error("Failed to copy:", err);
          toast.error("Failed to copy to clipboard");
        }
      );
    } catch (e) {
      console.log("Error", e);
      toast.error(e.message || "Failed to copy to clipboard");
    }
  };

  return (
    <OverlayTrigger
      placement={placement}
      overlay={
        <Tooltip id={`tooltip-${id}`}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "monospace", marginBottom: "5px" }}>
              {id}
            </div>
            <small style={{ fontSize: "0.75em" }}>Click to copy</small>
          </div>
        </Tooltip>
      }
    >
      <span
        style={{
          fontFamily: "monospace",
          fontSize: "0.85em",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          copyToClipboard(id, label);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          copyToClipboard(id, label);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "#007bff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "";
        }}
      >
        {id.substring(0, truncateLength)}...
        <FaCopy
          style={{
            fontSize: "0.75em",
            opacity: 0.6,
          }}
        />
      </span>
    </OverlayTrigger>
  );
};

export default CopyableId;
